import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Text } from "@twilio-paste/core/text";
import { useDispatch, useSelector } from "react-redux";

import { updatePreEngagementStep } from "../../store/actions/genericActions";
import { AppState, PrechatCard } from "../../store/definitions";
import { safeLinkHref } from "../../utils/safeLink";
import { BRAND } from "../../brands";
import {
    introStyles,
    titleStyles,
    formStyles,
    cardsContainerStyles,
    cardStyles,
    cardTitleStyles,
    cardSubtitleStyles,
    buttonContainerStyles
} from "../styles/PreEngagementFormPhase.styles";

/*
 * Id RESERVADO do card de promoções. Diferente de `help-center` e `lgpd`, este NÃO abre link: leva
 * à lista de promoções por dentro do próprio widget. O portal trata o mesmo id como fixo, para
 * ninguém herdá-lo por acidente ao criar um card chamado "Promoções".
 */
export const PROMOS_CARD_ID = "promocoes";

/* Default = card único de hoje. `help-center` sem url cai no FAQ da marca (comportamento atual). */
const DEFAULT_CARDS: PrechatCard[] = [
    { id: "help-center", title: "Central de Ajuda", subtitle: "Perguntas frequentes (FAQ)", enabled: true }
];

/*
 * URL do card: usa `card.url` se preenchida; senão, `help-center` cai no FAQ da marca e qualquer
 * outro card sem url é informativo (clique inerte). `safeLinkHref` bloqueia esquemas perigosos.
 */
const resolveCardHref = (card: PrechatCard): string | null => {
    const explicit = card.url && card.url.trim() ? card.url : "";
    const raw = explicit || (card.id === "help-center" ? BRAND.helpCenterUrl : "");
    return safeLinkHref(raw);
};

const openCard = (card: PrechatCard) => {
    const href = resolveCardHref(card);
    if (href) {
        window.open(href, "_blank", "noopener,noreferrer");
    }
};

/* Step 1: boas-vindas + cards de autoatendimento + botão de iniciar. */
export const WelcomeStep = ({ onAbrirPromocoes }: { onAbrirPromocoes: () => void }) => {
    const dispatch = useDispatch();
    const welcome = useSelector((state: AppState) => state.config?.prechat?.welcome);
    const configuredCards = useSelector((state: AppState) => state.config?.prechat?.cards);
    // Contagem vinda do brand-config (load). `undefined` = o servidor não mandou → sem contador.
    const promocoesVigentes = useSelector((state: AppState) => state.config?.promocoesVigentes);

    const welcomeTitle = welcome?.title;
    const welcomeSubtitle = welcome?.subtitle ?? "Em que podemos ajudar hoje?";
    /*
     * Promoções SEMPRE em primeiro, independentemente da ordem salva no pré-chat. É decisão de
     * produto (Mateus, 2026-08-06): é a porta que a casa quer em evidência, e deixá-la depender da
     * ordem da config faria o card cair pro fim assim que alguém acrescentasse outro.
     * O resto dos cards mantém a ordem que a marca configurou.
     */
    const cards = (configuredCards ?? DEFAULT_CARDS)
        .filter((card) => card.enabled)
        .sort((a, b) => Number(b.id === PROMOS_CARD_ID) - Number(a.id === PROMOS_CARD_ID));

    return (
        <Box {...formStyles} data-test="pre-engagement-welcome">
            {welcomeTitle ? (
                <Text {...titleStyles} as="h3">
                    {welcomeTitle}
                </Text>
            ) : (
                <Text {...titleStyles} as="h3">
                    Seja bem-vindo(a)!{" "}
                    <span role="img" aria-label="aceno">
                        👋
                    </span>
                </Text>
            )}
            <Text {...introStyles} as="p">
                {welcomeSubtitle}
            </Text>
            <Box {...cardsContainerStyles}>
                {cards.map((card) => (
                    <Box
                        key={card.id}
                        as="button"
                        type="button"
                        data-test={`pre-engagement-card-${card.id}`}
                        onClick={() => (card.id === PROMOS_CARD_ID ? onAbrirPromocoes() : openCard(card))}
                        {...cardStyles}
                    >
                        <Text as="span" {...cardTitleStyles}>
                            {card.title}
                            {/*
                              O contador aparece MESMO valendo zero (padrão "Avisos 0"): a porta
                              continua visível e o apostador sabe que está vazia, em vez de achar
                              que sumiu. Por isso o teste é `!== undefined`, e não um truthy — que
                              apagaria justamente o zero.
                            */}
                            {card.id === PROMOS_CARD_ID && promocoesVigentes !== undefined ? (
                                <span className="promo-contador" data-test="contador-promocoes">
                                    {promocoesVigentes}
                                </span>
                            ) : null}
                        </Text>
                        {card.subtitle ? (
                            <Text as="span" {...cardSubtitleStyles}>
                                {card.subtitle}
                            </Text>
                        ) : null}
                    </Box>
                ))}
            </Box>
            <Box {...buttonContainerStyles}>
                <Button
                    variant="primary"
                    data-test="syncia-start-service-button"
                    onClick={() => dispatch(updatePreEngagementStep(2))}
                >
                    Iniciar Atendimento
                </Button>
            </Box>
        </Box>
    );
};
